import { Editor } from 'slate';

import { formatListMarker, ORDERED_LIST_STYLES } from '../../extract-paragraphs/docx/list-utils';
import { advanceListCounter, isListLevel, isParagraphElement, Queries } from '../../slate-api';
import type { ParagraphElement } from '../../slate-api';

export type CastListMarkerInfo = {
  /** 1-based list nesting level. */
  listLevel: number;
  listMarker: string;
  listType: 'bullet' | 'numbered';
};

/**
 * Precompute the list marker for every list paragraph in the document using the
 * editor's own numbering rule (`advanceListCounter`) keyed per `listId`, so the
 * markers in the Cast match exactly what the editor renders on screen.
 *
 * Numbering is per `listId`: a numbered list keeps counting across an
 * intervening bullet list, table, or plain paragraph (those belong to other
 * lists or to none), and only a genuinely different `listId` starts a fresh
 * count. This mirrors `Queries.getListItemPath` / `Queries.listMarker` instead
 * of the old single running counter with reset heuristics, which wrongly
 * restarted numbering after any interruption.
 *
 * The map is keyed by the paragraph node, not `locationId`:
 * `extractCastFromAlineDoc` reads the document straight off the query editor, so
 * the blocks the extractor walks are the very nodes scanned here. (Keying by
 * `locationId` would also break tests, which give every paragraph the same
 * placeholder id.)
 */
export function computeListMarkers(editor: Editor): Map<ParagraphElement, CastListMarkerInfo> {
  const { listDefinitions } = Queries.getNumbering(editor);
  const countersByListId = new Map<string, number[]>();
  const markersByParagraph = new Map<ParagraphElement, CastListMarkerInfo>();

  const paragraphEntries = Editor.nodes<ParagraphElement>(editor, {
    at: [],
    match: isParagraphElement,
    voids: true,
  });

  for (const [paragraph] of paragraphEntries) {
    const listProps = Queries.renderedParagraphListProps(paragraph);
    if (!listProps) continue;

    const { listId, listLevel } = listProps;
    const oneBasedLevel = listLevel + 1;
    const listDefinition = listId in listDefinitions ? listDefinitions[listId] : undefined;

    /**
     * Without a definition we can't number the item, so fall back to a depth
     * bullet (matching the previous behavior for unknown lists).
     */
    if (!listDefinition) {
      markersByParagraph.set(paragraph, {
        listLevel: oneBasedLevel,
        listMarker: '*.'.repeat(oneBasedLevel),
        listType: 'bullet',
      });
      continue;
    }

    const counter = advanceListCounter(
      countersByListId.get(listId) ?? [],
      listLevel,
      listDefinition
    );
    countersByListId.set(listId, counter);

    const styleName = listDefinition[listLevel]?.style.name;
    if (!styleName || !ORDERED_LIST_STYLES.has(styleName)) {
      markersByParagraph.set(paragraph, {
        listLevel: oneBasedLevel,
        listMarker: '*.'.repeat(oneBasedLevel),
        listType: 'bullet',
      });
      continue;
    }

    const parts: string[] = [];
    for (let level = 0; level < counter.length; level++) {
      if (!isListLevel(level)) continue;
      const part = formatListMarker(counter[level], listDefinition[level]?.style.name);
      if (part !== '') parts.push(part);
    }
    markersByParagraph.set(paragraph, {
      listLevel: oneBasedLevel,
      listMarker: `${parts.join('.')}.`,
      listType: 'numbered',
    });
  }

  return markersByParagraph;
}
